import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { HelpHint } from './HelpHint';

/**
 * Add or edit a technician.
 *
 * The fields are ordered by how often they are wrong rather than by how the
 * table is laid out: name and number first, because a stale number is the
 * commonest reason a job goes unanswered, then the channels that decide how
 * they are reached, then the zones and skills that decide which jobs they are
 * offered.
 */

export type TechnicianDraft = {
  id?: string;
  name: string;
  phone: string;
  email: string;
  altPhone: string;
  channels: string[];
  zones: string[];
  skills: string[];
  maxConcurrent: number;
  certPoleExpiry: string;
  employedSince: string;
  isActive: boolean;
};

export const emptyTechnician = (): TechnicianDraft => ({
  name: '',
  phone: '',
  email: '',
  altPhone: '',
  // Email leads because it is the only live channel. See NOTIFICATIONS.md.
  channels: ['email'],
  zones: [],
  skills: [],
  maxConcurrent: 3,
  certPoleExpiry: '',
  employedSince: '',
  isActive: true,
});

const CHANNELS = [
  { id: 'email', label: 'Email', note: 'Live today' },
  { id: 'whatsapp', label: 'WhatsApp', note: 'Waiting on Meta approval' },
  { id: 'sms', label: 'SMS', note: 'Waiting on an account' },
];

export function TechnicianForm({
  draft,
  onChange,
  onDone,
  onCancel,
}: {
  draft: TechnicianDraft;
  onChange: (next: TechnicianDraft) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const editing = Boolean(draft.id);

  const { data: zonesData } = useQuery({ queryKey: ['zones'], queryFn: api.zones });
  const { data: jobTypesData } = useQuery({ queryKey: ['job-types'], queryFn: api.jobTypes });

  // The skills that actually exist are the ones some job type asks for. A
  // hand-maintained list would drift away from the catalogue silently.
  const skills = [
    ...new Set((jobTypesData?.jobTypes ?? []).flatMap((t) => t.requiredSkills ?? [])),
  ].sort();

  const set = <K extends keyof TechnicianDraft>(key: K, value: TechnicianDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const toggle = (key: 'channels' | 'zones' | 'skills', value: string) =>
    set(
      key,
      draft[key].includes(value) ? draft[key].filter((v) => v !== value) : [...draft[key], value],
    );

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: draft.name,
        phone: draft.phone,
        email: draft.email || undefined,
        altPhone: draft.altPhone || undefined,
        channels: draft.channels,
        zones: draft.zones,
        skills: draft.skills,
        maxConcurrent: draft.maxConcurrent,
        certPoleExpiry: draft.certPoleExpiry || undefined,
        employedSince: draft.employedSince || undefined,
      };
      return draft.id
        ? api.updateTechnician(draft.id, { ...payload, isActive: draft.isActive })
        : api.createTechnician(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['technicians'] });
      onDone();
    },
    onError: (e) => setError((e as Error).message),
  });

  const emailChosenWithoutAddress = draft.channels.includes('email') && !draft.email.trim();

  return (
    <form
      className="card space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <h3 className="font-heading text-lg">{editing ? `Edit ${draft.name}` : 'Add a technician'}</h3>

      {error && <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name" required>
          <input
            className="input w-full"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            required
            minLength={2}
          />
        </Field>

        <Field
          label="Phone number"
          required
          hint="Where their tickets go. 0712345678 or +254712345678 — both are stored the same way."
        >
          <input
            className="input w-full"
            value={draft.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="0712345678"
            required
          />
        </Field>

        <Field
          label="Email address"
          hint="Required if email is one of their channels — which today means required to dispatch them at all."
        >
          <input
            type="email"
            className={`input w-full ${emailChosenWithoutAddress ? 'border-danger-400' : ''}`}
            value={draft.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="name@gospeednet.co.ke"
          />
          {emailChosenWithoutAddress && (
            <p className="mt-1 text-xs text-danger-600">
              Email is selected below, so an address is needed.
            </p>
          )}
        </Field>

        <Field label="Alternative phone" hint="The one that actually answers, more often than anyone plans for.">
          <input
            className="input w-full"
            value={draft.altPhone}
            onChange={(e) => set('altPhone', e.target.value)}
          />
        </Field>
      </div>

      <fieldset>
        <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          How they are sent jobs
          <HelpHint>
            Tried in order. If the first fails every retry, the system falls back to the next one,
            and tells a dispatcher if it runs out.
          </HelpHint>
        </legend>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((channel) => (
            <button
              key={channel.id}
              type="button"
              onClick={() => toggle('channels', channel.id)}
              className={`rounded-xl border px-3 py-2 text-left text-sm ${
                draft.channels.includes(channel.id)
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-border hover:border-muted-foreground'
              }`}
            >
              <span className="block font-medium">{channel.label}</span>
              <span className="block text-xs text-muted-foreground">{channel.note}</span>
            </button>
          ))}
        </div>
        {draft.channels.length === 0 && (
          <p className="mt-2 text-xs text-danger-600">
            Pick at least one, or they cannot be sent anything.
          </p>
        )}
      </fieldset>

      <fieldset>
        <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          Zones they cover
          <HelpHint>
            Zones, not individual areas — whoever covers Suneka covers Itierio. Leaving this empty
            still lets a dispatcher assign them; it only removes them from the suggestions.
          </HelpHint>
        </legend>
        <div className="flex flex-wrap gap-2">
          {(zonesData?.zones ?? []).map((zone) => (
            <Chip
              key={zone.code}
              on={draft.zones.includes(zone.code)}
              onClick={() => toggle('zones', zone.code)}
            >
              {zone.code} · {zone.name}
            </Chip>
          ))}
        </div>
      </fieldset>

      {skills.length > 0 && (
        <fieldset>
          <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">
            Skills
            <HelpHint>
              A job type that needs a skill will not suggest anyone without it. The dispatcher can
              still override.
            </HelpHint>
          </legend>
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <Chip
                key={skill}
                on={draft.skills.includes(skill)}
                onClick={() => toggle('skills', skill)}
              >
                {skill.replace(/_/g, ' ').toLowerCase()}
              </Chip>
            ))}
          </div>
        </fieldset>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Jobs at once" hint="Beyond this they stop being suggested. It does not stop a dispatcher assigning anyway.">
          <input
            type="number"
            min={1}
            max={20}
            className="input w-full"
            value={draft.maxConcurrent}
            onChange={(e) => set('maxConcurrent', Number(e.target.value))}
          />
        </Field>

        <Field
          label="Pole certificate expires"
          hint="Leave blank if they do not climb. An expired certificate blocks pole work and warns 30 days out."
        >
          <input
            type="date"
            className="input w-full"
            value={draft.certPoleExpiry}
            onChange={(e) => set('certPoleExpiry', e.target.value)}
          />
        </Field>

        <Field
          label="Employment started"
          hint="What leave accrues from — 21 working days a year, a twelfth each month. Left blank, the balance counts from when this record was created and says on screen that it is guessing."
        >
          <input
            type="date"
            className="input w-full"
            value={draft.employedSince}
            onChange={(e) => set('employedSince', e.target.value)}
          />
        </Field>
      </div>

      {editing && (
        <label className="flex items-start gap-2 rounded-xl bg-muted/40 p-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          <span>
            <span className="font-medium">On shift</span>
            <span className="block text-muted-foreground">
              Taking someone off shift removes them from suggestions. It is refused while they still
              have open jobs — reassign those first.
            </span>
          </span>
        </label>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          className="btn-primary"
          disabled={save.isPending || draft.channels.length === 0 || emailChosenWithoutAddress}
        >
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add technician'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-sm font-medium">
        {label}
        {required && <span className="text-danger-600">*</span>}
        {hint && <HelpHint>{hint}</HelpHint>}
      </span>
      {children}
    </label>
  );
}

function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs ${
        on
          ? 'border-primary-500 bg-primary-50 text-primary-800'
          : 'border-border text-muted-foreground hover:border-muted-foreground'
      }`}
    >
      {children}
    </button>
  );
}
